import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero, Section } from "@/components/Layout";
import { POLICIES, BUSINESS } from "@/data/site";
import { CombinedPolicyForm } from "@/components/PolicyForm";

const FORM_SLUGS = ["agreement", "emergency"];

const Block = ({ b }) => {
  switch (b.t) {
    case "h":
      return <h2 className="font-heading text-xl sm:text-2xl font-semibold text-foreground mt-9 mb-3 first:mt-0">{b.x}</h2>;
    case "sub":
      return <h3 className="font-heading text-lg font-semibold text-foreground mt-6 mb-2">{b.x}</h3>;
    case "p":
      return <p className="text-base text-muted-foreground leading-relaxed mb-4">{b.x}</p>;
    case "ul":
      return (
        <ul className="list-disc pl-5 space-y-1.5 mb-4 text-base text-muted-foreground marker:text-primary">
          {b.x.map((item) => <li key={item} className="leading-relaxed">{item}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-5 space-y-2 mb-4 text-base text-muted-foreground marker:text-primary marker:font-medium">
          {b.x.map((item) => <li key={item} className="leading-relaxed pl-1">{item}</li>)}
        </ol>
      );
    case "check":
      return (
        <ul className="space-y-2.5 mb-5">
          {b.x.map((item) => (
            <li key={item} className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
              <span className="mt-1 w-4 h-4 shrink-0 rounded-[3px] border-2 border-primary/60" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "field":
      return (
        <div className="mb-5">
          <span className="text-sm font-medium text-foreground">{b.x}</span>
          <div className="mt-1.5 border-b-2 border-dotted border-border h-6" />
        </div>
      );
    case "lines":
      return (
        <div className="mb-5 space-y-5">
          {Array.from({ length: b.n }).map((_, k) => <div key={`line-${k}`} className="border-b-2 border-dotted border-border h-6" />)}
        </div>
      );
    case "contact":
      return (
        <div className="mb-4 rounded-2xl bg-accent/50 p-5 text-base text-muted-foreground leading-relaxed">
          <p className="font-semibold text-foreground">{BUSINESS.name}</p>
          <p>Email: <a href={`mailto:${BUSINESS.email}`} className="text-primary hover:underline">{BUSINESS.email}</a></p>
          <p>Phone: <a href={`tel:${BUSINESS.phoneRaw}`} className="text-primary hover:underline">{BUSINESS.phoneDisplay}</a></p>
          <p>Service Area: {BUSINESS.serviceArea}</p>
        </div>
      );
    case "hr":
      return <hr className="my-8 border-border" />;
    default:
      return null;
  }
};

const Policies = () => {
  const { slug } = useParams();

  if (FORM_SLUGS.includes(slug)) {
    return (
      <div data-testid="policy-page">
        <PageHero
          eyebrow="Cozy Pet Care"
          title="Pet Care Agreement & Emergency Authorization"
        />
        <Section className="py-14 sm:py-16 max-w-3xl">
          <CombinedPolicyForm scrollTo={slug === "emergency" ? "emergency" : null} />
          <Button asChild variant="ghost" className="mt-8 rounded-full" data-testid="policy-back-btn">
            <Link to="/" data-testid="policy-back"><ArrowLeft className="w-4 h-4 mr-2" /> Back to home</Link>
          </Button>
        </Section>
      </div>
    );
  }

  const policy = POLICIES[slug];

  if (!policy) {
    return (
      <div data-testid="policy-page">
        <PageHero eyebrow="Policy" title="Policy Not Found" />
        <Section className="py-16 max-w-3xl">
          <p className="text-lg text-muted-foreground">This policy is being prepared and will be published soon.</p>
          <Button asChild variant="ghost" className="mt-8 rounded-full">
            <Link to="/" data-testid="policy-back"><ArrowLeft className="w-4 h-4 mr-2" /> Back to home</Link>
          </Button>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="policy-page">
      <PageHero eyebrow="Cozy Pet Care" title={policy.title} subtitle={policy.updated} />
      <Section className="py-14 sm:py-16 max-w-3xl">
        <div className="rounded-3xl bg-card p-7 sm:p-10 soft-shadow" data-testid="policy-content">
          {policy.blocks.map((b, i) => <Block key={`${b.t}-${i}`} b={b} />)}
        </div>
        <Button asChild variant="ghost" className="mt-8 rounded-full" data-testid="policy-back-btn">
          <Link to="/" data-testid="policy-back"><ArrowLeft className="w-4 h-4 mr-2" /> Back to home</Link>
        </Button>
      </Section>
    </div>
  );
};

export default Policies;
